<template>
  <div class="messagebox-footer" :class="{ exited }">
    <div v-if="exited" class="exit-detail">{{ exitDetail }}</div>
    <div class="footer-icon">
      <div class="icon-left">
        <UpLoadImage
          customUpload
          chatToType="private"
          :disabled="!isManualIntervention || exited"
          @select="handleSendImage"
        />
        <CommonWords
          ref="commonWordsRef"
          :disabled="!isManualIntervention || exited"
          @selectWords="handleSelectWords"
        />
      </div>
      <div class="footer-actions">
        <a-button
          type="primary"
          class="intervention-button"
          :class="{ 'is-manual': isManualIntervention }"
          :ghost="isManualIntervention"
          :disabled="exited"
          :loading="takeoverLoading"
          @click="emit('toggle-manual-intervention')"
        >
          {{ isManualIntervention ? '结束人工服务，AI继续服务' : '暂停AI服务，人工介入' }}
        </a-button>
        <a-button v-if="isManualIntervention" type="primary" :disabled="exited" @click="handleSend">
          发送
        </a-button>
      </div>
    </div>
    <div class="footer-send">
      <a-textarea
        ref="editor"
        v-model:value="draft"
        class="send-text"
        placeholder="请输入消息内容后按Enter键发送"
        :maxlength="MAX_MESSAGE_LENGTH"
        :disabled="!isManualIntervention || exited"
        @keydown.enter="handleEnter"
      />
    </div>
    <a-modal
      v-model:visible="sendModalVisible"
      class="wechat-send-modal"
      wrap-class-name="wechat-send-modal-wrap"
      title="提示"
      :width="520"
      :footer="null"
      centered
      @cancel="closeSendModal"
    >
      <div class="send-modal-content">
        <div class="send-preview">
          <img v-if="sendingType === 'image'" :src="imagePreviewUrl" alt="发送图片" />
          <span v-else>{{ sendingContent }}</span>
        </div>
        <div class="sending-status" :class="{ failed: sendStatus === 'failed' }">
          <span v-if="sendStatus === 'sending'" class="sending-spinner" />
          <CloseCircleFilled v-else class="failed-icon" />
          <span>{{ sendStatus === 'failed' ? '发送失败' : '发送中' }}</span>
        </div>
      </div>
      <div class="send-delay-tip" :class="{ failed: sendStatus === 'failed' }">
        {{
          sendStatus === 'failed'
            ? '消息发送失败，可尝试重新发送消息。'
            : '微信渠道发送消息存在一定延迟（预计1-2min），请稍后查看微信私聊群即可，不要重复发送消息。'
        }}
      </div>
      <div class="send-modal-footer" :class="{ failed: sendStatus === 'failed' }">
        <a-button v-if="sendStatus === 'failed'" type="primary" @click="retrySend">
          重新发送
        </a-button>
        <a-button size="large" v-else @click="closeSendModal">知道了</a-button>
      </div>
    </a-modal>
  </div>
</template>

<script setup>
import { defineEmits, defineProps, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { CloseCircleFilled } from '@ant-design/icons-vue'
import UpLoadImage from '../upLoadImage/index.vue'
import CommonWords from '../commonWords/index.vue'

const props = defineProps({
  quoteRequest: {
    type: Object,
    default: () => ({ id: 0, text: '' })
  },
  isManualIntervention: { type: Boolean, default: false },
  takeoverLoading: { type: Boolean, default: false },
  sendCompletedId: { type: Number, default: 0 },
  sendFailedId: { type: Number, default: 0 },
  exited: { type: Boolean, default: false },
  exitDetail: { type: String, default: '' }
})
const emit = defineEmits([
  'send-text',
  'send-image',
  'toggle-manual-intervention',
  'select-common-word'
])
const draft = ref('')
const editor = ref(null)
const commonWordsRef = ref(null)
const sendModalVisible = ref(false)
const sendingType = ref('text')
const sendingContent = ref('')
const imagePreviewUrl = ref('')
const sendStatus = ref('sending')
const sendingPayload = ref(null)
const MAX_MESSAGE_LENGTH = 4000

const clearImagePreview = () => {
  if (imagePreviewUrl.value) URL.revokeObjectURL(imagePreviewUrl.value)
  imagePreviewUrl.value = ''
}

const openSendModal = ({ type, content = '', file }) => {
  clearImagePreview()
  sendingType.value = type
  sendingContent.value = content
  sendStatus.value = 'sending'
  sendingPayload.value = type === 'image' ? { file } : content
  if (type === 'image' && file) imagePreviewUrl.value = URL.createObjectURL(file)
  sendModalVisible.value = true
}

const closeSendModal = () => {
  sendModalVisible.value = false
  clearImagePreview()
}

const retrySend = () => {
  sendStatus.value = 'sending'
  if (sendingType.value === 'image') emit('send-image', sendingPayload.value)
  else emit('send-text', sendingPayload.value)
}

watch(
  () => props.quoteRequest,
  request => {
    if (!request?.id || !request.text) return
    draft.value = `${draft.value}${request.text}`
    nextTick(() => {
      if (!editor.value) return
      editor.value.focus()
      editor.value.setSelectionRange(draft.value.length, draft.value.length)
    })
  }
)

const handleSend = () => {
  if (!props.isManualIntervention || props.exited) return
  const content = draft.value.trim()
  if (!content) return
  openSendModal({ type: 'text', content })
  emit('send-text', content)
  draft.value = ''
}

const handleEnter = event => {
  if (event.shiftKey || event.isComposing) return
  event.preventDefault()
  handleSend()
}

const handleSendImage = payload => {
  if (!payload?.file) return
  openSendModal({ type: 'image', file: payload.file })
  emit('send-image', payload)
}

const handleSelectWords = record => {
  if (!props.isManualIntervention || props.exited) return
  draft.value = `${draft.value}${record?.chatContent || ''}`.slice(0, MAX_MESSAGE_LENGTH)
  commonWordsRef.value?.closeDrawer()
  emit('select-common-word', record)
  nextTick(() => {
    if (!editor.value) return
    editor.value.focus()
    editor.value.setSelectionRange(draft.value.length, draft.value.length)
  })
}

watch(
  () => props.sendCompletedId,
  (value, oldValue) => {
    if (value !== oldValue && sendingType.value === 'text') closeSendModal()
  }
)

watch(
  () => props.sendFailedId,
  (value, oldValue) => {
    if (value !== oldValue && sendModalVisible.value) sendStatus.value = 'failed'
  }
)

onBeforeUnmount(clearImagePreview)
</script>

<style scoped lang="scss">
.messagebox-footer {
  width: 100%;
  height: 200px;
  flex: 0 0 200px;
  color: rgba(0, 0, 0, 0.65);
  display: flex;
  flex-flow: column;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}

.messagebox-footer.exited {
  height: 242px;
  flex-basis: 242px;
}

.exit-detail {
  width: 100%;
  height: 42px;
  flex: 0 0 42px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  background: #fff5ea;
  color: #333;
  font-size: 14px;
  font-weight: 500;
}

.footer-icon {
  width: 100%;
  flex: 0 0 58px;
  height: 58px;
  box-sizing: border-box;
  padding: 10px 20px 8px 0;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  overflow: auto;

  &::-webkit-scrollbar {
    display: none;
  }

  .icon-left {
    display: flex;
    align-items: center;
  }

  .footer-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  ::v-deep(.intervention-button) {
    min-width: 207px;
    height: 35px;
    padding: 0 24px;
    box-sizing: border-box;
    border-radius: 4px;
    font-size: 16px;
    white-space: nowrap;
  }

  ::v-deep(.ant-btn-primary) {
    width: 79px;
    height: 35px;
    border-radius: 4px;
    font-size: 16px;
  }

  ::v-deep(.intervention-button.is-manual) {
    width: 241px;
    min-width: 241px;
  }
}

.footer-send {
  width: 100%;
  flex: 1;
  min-height: 0;
  box-sizing: border-box;
  padding: 14px 19px;
  overflow: hidden;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;

  .send-text {
    display: block;
    width: 100%;
    height: 100%;
    min-height: 100%;
    padding: 0;
    resize: none;
    box-sizing: border-box;
    border: none;
    outline: none;
    box-shadow: none;
    background: #fafafa;
    font-size: 14px;
    line-height: 1.5;
    text-align: left;
    position: relative;
    z-index: 1;
    overflow-y: auto;

    &:disabled {
      color: #999;
      cursor: not-allowed;
    }
  }
}

.send-modal-content {
  min-height: 170px;
  max-height: 420px;
  padding: 24px 20px;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
  background: #f2f4f7;
}

.send-preview {
  width: fit-content;
  max-width: calc(100% - 40px);
  margin-left: auto;
  padding: 12px 16px;
  box-sizing: border-box;
  border-radius: 8px 8px 0;
  background: #dceeff;
  color: #333;
  font-size: 14px;
  line-height: 20px;
  white-space: pre-wrap;
  word-break: break-word;

  img {
    display: block;
    max-width: 160px;
    max-height: 120px;
    margin-left: auto;
    object-fit: contain;
  }
}

.sending-status {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  color: #4b9eff;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
}

.sending-status.failed {
  color: #ff5a5a;
}

.failed-icon {
  width: 24px;
  height: 24px;
  color: #ff5a5a;
  font-size: 24px;
}

.sending-spinner {
  width: 24px;
  height: 24px;
  box-sizing: border-box;
  border: 2px solid #d9eaff;
  border-top-color: #4b9eff;
  border-radius: 50%;
  animation: wechat-send-spin 0.8s linear infinite;
}

.send-delay-tip {
  padding: 12px;
  box-sizing: border-box;
  background: #fff7ee;
  color: #fc8a0f;
  font-size: 16px;
  font-weight: 500;
  line-height: 21px;
}

.send-delay-tip.failed {
  background: #ffe8e8;
  color: #ff5a5a;
}

.send-modal-footer {
  height: 52px;
  padding: 10px 20px;
  box-sizing: border-box;
  text-align: right;

  .ant-btn {
    width: 96px;
    height: 34px;
    border-color: #1890ff;
    border-radius: 4px;
    color: #1890ff;
    font-size: 15px;
  }
}

.send-modal-footer.failed {
  text-align: center;

  .ant-btn {
    border-color: #1890ff;
    color: #fff;
  }
}

@keyframes wechat-send-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>

<style lang="scss">
.wechat-send-modal-wrap {
  .ant-modal-content {
    overflow: hidden;
    border-radius: 4px;
  }

  .ant-modal-header {
    height: 49px;
    padding: 13px 16px;
    border-bottom: 1px solid #f0f0f0;
  }

  .ant-modal-title {
    color: rgba(0, 0, 0, 0.85);
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
  }

  .ant-modal-close-x {
    width: 52px;
    height: 49px;
    line-height: 49px;
  }

  .ant-modal-body {
    padding: 0;
  }
}
</style>
